import I18n from "i18n-js";
import { arrayOf, func, shape, number, bool } from "prop-types";
import React from "react";
import DateInput from "../../common/components/form/DateInput";
import Select from "../../common/components/form/Select";
import List from "../../common/components/layout/List";
import { alphabetically, uniq } from "../../common/utils/filterUtils";
import ClientUtils from "../../company/utils/ClientUtils";
import Roles from "../../users/models/Roles";
import UserUtils from "../../users/utils/UserUtils";
import InterventionType from "../models/InterventionType";
import StatelessTable from "../../common/components/elements/StatelessTable";
import StatelessTextInput from "../../common/components/form/StatelessTextInput";

export default class InterventionList extends List {
  static propTypes = {
    auth: shape().isRequired,
    interventions: shape().isRequired,
    interventionsList: arrayOf(shape).isRequired,
    users: shape().isRequired,
    clientsList: arrayOf(shape).isRequired,
    getClientsList: func.isRequired,
    userProfile: shape().isRequired,
    getInterventionList: func.isRequired,
    getUsersList: func.isRequired,
    getFilters: func,
    listFilters: shape(),
    getCount: func,
    count: number,
    location: shape().isRequired,
    clientSideFiltering: bool,
  };

  constructor(props) {
    super(props, {
      deletedInterventions: [],
    });

    this.shouldRenderTechFilter = props.userProfile.roles.includes(
      Roles.MANAGER,
    );

    this.refreshDataState = this.refreshDataState.bind(this);
    this.dataTableRow = this.dataTableRow.bind(this);
    this.removeIntervention = this.removeIntervention.bind(this);

    this.onUpdateUrl = this.onUpdateUrl.bind(this);
    this.getFilterFilters = this.getFilterFilters.bind(this);
    this.getFilterRequestType = this.getFilterRequestType.bind(this);
    this.fetchData = this.fetchData.bind(this);
  }

  /**
   * {@inheritdoc}
   */
  componentDidMount() {
    super.componentDidMount(this.fetchData);
  }

  fetchData() {
    const {
      getInterventionList,
      getUsersList,
      getFilters,
      getCount,
      getClientsList,
      clientSideFiltering,
    } = this.props;

    const { filters } = this.state;

    if (clientSideFiltering) {
      getInterventionList();
      getClientsList();
    } else {
      getInterventionList(filters);
      getFilters(filters);
      getCount(filters);
    }

    if (this.shouldRenderTechFilter) {
      getUsersList();
    }
  }

  /**
   * @param {Object} prevState
   */
  refreshDataState(prevState) {
    const { client, site } = this.state.filters;

    let newFilters = {};

    if (prevState?.oldFilters?.client !== client) {
      newFilters = {
        ...this.state.filters,
        site: null,
        installation: null,
      };
    }

    if (!site || prevState?.oldFilters?.site !== site) {
      newFilters = {
        ...this.state.filters,
        ...newFilters,
        installation: null,
      };
    }

    if (Object.keys(newFilters).length > 0) {
      this.setState({ filters: newFilters });
      this.updateUrl(newFilters, prevState.oldFilters);
    }
  }

  getSitesFromInterventions() {
    const sites = {};

    this.props.interventionsList
      .filter(this.getFilterBySelect("client"))
      .forEach((intervention) => {
        sites[intervention.site.uuid] = intervention.site;
      });

    return Object.values(sites).sort((a, b) => {
      return alphabetically(a.designation, b.designation);
    });
  }

  getInstallationsFromInterventions() {
    const installations = {};

    this.props.interventionsList
      .filter(this.getFilterBySelect("client"))
      .filter(this.getFilterBySelect("site"))
      .forEach((intervention) => {
        installations[intervention.installation.uuid] =
          intervention.installation;
      });

    return Object.values(installations).sort((a, b) =>
      alphabetically(a.designation, b.designation),
    );
  }

  getServiceProvidersFromInterventions() {
    const serviceProviders = {};

    this.props.interventionsList
      .filter(this.getFilterBySelect("client"))
      .filter(this.getFilterBySelect("site"))
      .filter(this.getFilterBySelect("installation"))
      .forEach((intervention) => {
        const provider = intervention.serviceProvider;

        if (
          provider &&
          this.props.auth.userProfile.clientUuid !== provider.uuid
        ) {
          serviceProviders[provider.uuid] = provider;
        }
      });

    return Object.values(serviceProviders).sort((a, b) =>
      alphabetically(ClientUtils.renderLabel(a), ClientUtils.renderLabel(b)),
    );
  }

  getTechnicianList() {
    const { interventionsList, users } = this.props;
    return uniq(interventionsList, "userUuid")
      .map((intervention) =>
        users.list.find((user) => user.uuid === intervention.userUuid),
      )
      .filter((a) => a)
      .sort((a, b) => alphabetically(a.lastname, b.lastname));
  }

  getInstallationLabel(installation) {
    return `${installation.mark || ""} (${installation.designation})`.trim();
  }

  renderTechnician(userUuid) {
    if (this.shouldRenderTechFilter) {
      const technician = this.props.users.list.find(
        (user) => user.uuid === userUuid,
      );
      return technician ? (
        `${technician.firstname} ${technician.lastname}`
      ) : (
        <span className="text-muted" key="external-operator">
          {I18n.t("pages.interventions.list.external_operator")}
        </span>
      );
    }
    return null;
  }

  renderActions() {
    throw new Error("You must implement method renderActions");
  }

  removeIntervention(interventionUuid) {
    this.setState({
      deletedInterventions: [
        ...this.state.deletedInterventions,
        interventionUuid,
      ],
    });
  }

  /**
   * @param {Object} option
   *
   * @return {Element}
   */
  renderFilterUserOption(option) {
    return (
      <option key={`option-${option.uuid}`} value={option.uuid}>
        {UserUtils.renderLabel(option)}
      </option>
    );
  }

  selectFilters() {
    const { listFilters, clientsList, clientSideFiltering, interventionsList } = this.props;
    const clientOptions = listFilters ? listFilters.clients : clientsList;

    const siteOptions = clientSideFiltering
      ? this.getSitesFromInterventions()
      : listFilters.sites;

    const installationOptions = clientSideFiltering
      ? this.getInstallationsFromInterventions()
      : listFilters.installations;

    const userOptions = clientSideFiltering
      ? this.getTechnicianList()
      : listFilters.users;

    const providerOptions = clientSideFiltering
      ? this.getServiceProvidersFromInterventions()
      : listFilters.providers;

    const typeOptions = clientSideFiltering
      ? uniq(interventionsList, "type")
      : listFilters.types;

    return { clientOptions, siteOptions, installationOptions, userOptions, providerOptions, typeOptions };
  }

  /**
   * {@inheritdoc}
   */
  renderFilters() {
    const { filters } = this.state;

    const { clientOptions, siteOptions, installationOptions, userOptions, providerOptions, typeOptions } =
      this.selectFilters();

    const allFilters = [
      <Select
        key="filterByClient"
        id="filterByClient"
        onChange={(event) =>
          this.onChangeFilter(
            event,
            "client",
            this.refreshDataState,
            false,
            true,
          )
        }
        label={I18n.t("filters.by_client")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {ClientUtils.renderLabel(option)}
          </option>
        )}
        options={clientOptions ?? []}
        defaultValue={filters.client}
        placeholder={I18n.t("filters.by_client:default")}
        optional
      />,
      <Select
        key="filterBySite"
        id="filterBySite"
        onChange={(event) =>
          this.onChangeFilter(event, "site", this.refreshDataState, false, true)
        }
        label={I18n.t("filters.by_site")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {option.designation}
          </option>
        )}
        // options={this.getSitesFromInterventions()}
        options={siteOptions ?? []}
        defaultValue={filters.site}
        placeholder={I18n.t("filters.by_site:default")}
        optional
      />,
      <Select
        key="filterByInstallation"
        id="filterByInstallation"
        onChange={(event) => this.onChangeFilter(event, "installation")}
        label={I18n.t("filters.by_installation")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {this.getInstallationLabel(option)}
          </option>
        )}
        // options={this.getInstallationsFromInterventions()}
        options={installationOptions ?? []}
        defaultValue={filters.installation}
        placeholder={I18n.t("filters.by_installation:default")}
        optional
      />,
      <Select
        key="filterByServiceProvider"
        id="filterByServiceProvider"
        onChange={(event) => this.onChangeFilter(event, "serviceProvider")}
        label={I18n.t("filters.by_service_provider")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {option.legalCompanyName}
          </option>
        )}
        options={providerOptions ?? []}
        defaultValue={filters.serviceProvider}
        placeholder={I18n.t("filters.by_service_provider:default")}
        optional
      />,
      <Select
        key="filterByInterventionType"
        id="filterByInterventionType"
        onChange={(event) => this.onChangeFilter(event, "type")}
        label={I18n.t("filters.by_intervention_type")}
        renderOption={(option) => (
          <option key={option.type} value={option.type}>
            {I18n.t(InterventionType.readableFor(option.type))}
          </option>
        )}
        options={typeOptions ?? []}
        defaultValue={filters.type}
        placeholder={I18n.t("filters.by_intervention_type:default")}
        optional
      />,
      <DateInput
        key="filterByStartDate"
        id="filterByStartDate"
        onChange={(date) => {
          this.onChangeDateFilter(date, "startDate");
        }}
        label={I18n.t("filters.by_start_date")}
        defaultValue={filters.startDate && new Date(Number(filters.startDate))}
        legend
        optional
        isClearable
      />,
      <DateInput
        key="filterByEndDate"
        id="filterByEndDate"
        onChange={(date) => this.onChangeDateFilter(date, "endDate")}
        label={I18n.t("filters.by_end_date")}
        defaultValue={filters.endDate && new Date(Number(filters.endDate))}
        legend
        optional
        isClearable
      />,
      <StatelessTextInput
        key="filterByObservation"
        id="filterByObservation"
        onChange={(event) => this.onChangeFilter(event, "observations")}
        label={I18n.t("filters.by_observation")}
        defaultValue={filters.observations}
        optional
      />,
      <StatelessTextInput
        key="filterByInterventionSheetNumber"
        id="filterByInterventionSheetNumber"
        onChange={(event) => this.onChangeFilter(event, "interventionSheetNumber")}
        label={I18n.t("filters.by_intervention_sheet_number")}
        defaultValue={filters.interventionSheetNumber}
        optional
      />,
      <StatelessTextInput
        key="filterByGroupedInterventionSheetNumber"
        id="filterByGroupedInterventionSheetNumber"
        onChange={(event) => this.onChangeFilter(event, "groupedInterventionSheetNumber")}
        label={I18n.t("filters.by_grouped_intervention_sheet_number")}
        defaultValue={filters.groupedInterventionSheetNumber}
        optional
      />,
    ];

    if (this.shouldRenderTechFilter) {
      // userOptions
      //   .map((intervention) =>
      //     users.list.find((user) => user.uuid === intervention.userUuid),
      //   )
      //   .filter((a) => a)
      //   .sort((a, b) => alphabetically(a.lastname, b.lastname));

      allFilters.push(
        <Select
          key="filterByUser"
          id="filterByUser"
          defaultValue={filters.user}
          onChange={(event) => this.onChangeFilter(event, "user")}
          label={I18n.t("filters.by_user")}
          renderOption={this.renderFilterUserOption}
          options={userOptions ?? []}
          placeholder={I18n.t("filters.by_user:default")}
          optional
        />,
      );
    }

    return allFilters;
  }

  getFilteredList() {
    const { clientSideFiltering } = this.props;
    const list = this.props.interventionsList.filter(
      (intervention) =>
        !this.state.deletedInterventions.includes(intervention.uuid),
    );

    if (clientSideFiltering) {
      return list
        .filter(this.getFilterBySelect("client"))
        .filter(this.getFilterBySelect("site"))
        .filter(this.getFilterBySelect("installation"))
        .filter(this.getFilterBySelect("serviceProvider"))
        .filter(this.getFilterByText("observation"))
        .filter(this.getFilterBySelect("type", false))
        .filter(this.getFilterByDate("creation"))
        .filter(this.getFilterBySelect("user"));
    } else {
      return list;
    }
  }

  /**
   * {@inheritdoc}
   */
  renderContent() {
    const { interventions, users, clientSideFiltering } = this.props;

    if (clientSideFiltering && interventions.inProgress) {
      return <div className="loader" />;
    }

    if (interventions.error || (this.shouldRenderTechFilter && users.error)) {
      return <p>{I18n.t("common.loadingApiError")}</p>;
    }

    const serverSideFilteringProps = {
      manualPagination: true,
      dataCount: interventions.count,
      filtersInProgress: interventions.filtersInProgress,
    };

    return (
      <StatelessTable
        emptyContent={I18n.t("pages.interventions.list.empty")}
        data={this.getFilteredList()}
        headData={this.dataTableHead()}
        rowData={this.dataTableRow}
        filters={this.renderFilters()}
        pagination={true}
        onPageChanged={this.onPageChanged}
        currentPage={this.getCurrentPage()}
        actions={this.renderActions()}
        inProgress={interventions.inProgress}
        {...(clientSideFiltering ? {} : serverSideFilteringProps)}
      />
    );
  }

  onUpdateUrl(newFilters, oldFilters) {
    const { getInterventionList, getCount, clientSideFiltering } = this.props;

    if (clientSideFiltering) {
      return;
    }

    this.getFilterFilters(newFilters);

    if (
      oldFilters.client !== newFilters.client ||
      oldFilters.site !== newFilters.site
    ) {
      this.fetchFilters(newFilters, oldFilters);
    }
    getInterventionList(newFilters);
    getCount(newFilters);
  }

  fetchFilters(filters, oldFilters = {}) {
    const { getFilters } = this.props;

    const filterType = this.getFilterRequestType(oldFilters, filters);

    getFilters({ ...this.getFilterFilters(filters), filterType });
  }

  getFilterFilters(filters) {
    const filterSubset = ["client", "site"];

    return filterSubset
      .filter((key) => key in filters)
      .reduce((subset, key) => ((subset[key] = filters[key]), subset), {});
  }

  // TODO make it an enum class?
  getFilterRequestType(oldFilters, newFilters) {
    if (!oldFilters) return;
    if (oldFilters.client !== newFilters.client) {
      return "sites";
    } else if (oldFilters.site !== newFilters.site) {
      return "installations";
    }
  }

  /**
   * {@inheritdoc}
   */
  renderTitle() {
    return I18n.t("pages.interventions.list.title");
  }
}
